// RevenueCat SDK wrapper — lazy native-only init so web builds don't crash.
// Web users hit our existing Stripe/Razorpay flow on /subscription. Native iOS
// users go through this RevenueCat path.
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { API } from '../App';

const IOS_KEY     = process.env.REACT_APP_REVENUECAT_IOS_KEY;
const ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY;
export const ENTITLEMENT_ID = process.env.REACT_APP_REVENUECAT_ENTITLEMENT_ID || 'janani pro';

/** Pick the correct RevenueCat public SDK key for the running platform.
 *  RevenueCat gives you TWO keys — one per store — and mixing them up is a
 *  silent failure (getOfferings returns empty). */
const platformKey = () => {
  if (Capacitor.getPlatform() === 'android') return ANDROID_KEY;
  if (Capacitor.getPlatform() === 'ios')     return IOS_KEY;
  return null; // web / other
};

let _configured = false;
let _purchasesMod = null;

const getPurchases = async () => {
  if (!Capacitor.isNativePlatform()) return null;
  if (_purchasesMod) return _purchasesMod;
  try {
    const mod = await import('@revenuecat/purchases-capacitor');
    _purchasesMod = mod;
    return mod;
  } catch (e) {
    console.warn('RevenueCat plugin not available:', e);
    return null;
  }
};

export const isNativeIAPSupported = () => Capacitor.isNativePlatform();

export const configureRevenueCat = async (userId) => {
  const mod = await getPurchases();
  const key = platformKey();
  if (!mod || !key) return false;
  try {
    // Avoid reconfiguring if we already did for the same user.
    if (_configured) {
      if (userId) await mod.Purchases.logIn({ appUserID: userId });
      return true;
    }
    await mod.Purchases.setLogLevel({ level: mod.LOG_LEVEL.WARN });
    await mod.Purchases.configure({ apiKey: key, appUserID: userId || undefined });
    _configured = true;
    return true;
  } catch (e) {
    console.error('RevenueCat configure failed:', e);
    return false;
  }
};

/** Ensure the RevenueCat singleton is configured before any offerings/purchase
 *  call. `configure()` is fired (non-awaited) at login, so a paywall opened
 *  quickly after can race ahead of it — this closes that gap. */
const ensureConfigured = async () => {
  if (_configured) return true;
  return await configureRevenueCat();
};

export const getOfferings = async () => {
  const mod = await getPurchases();
  if (!mod) return null; // web / plugin unavailable — caller handles
  await ensureConfigured();
  // NOTE: intentionally NOT swallowing errors here. A thrown error means we
  // couldn't reach the store (transient/network/not-configured) vs. a returned
  // empty offering which means no plans are configured yet — the paywall shows
  // different copy for each.
  const result = await mod.Purchases.getOfferings();
  return result?.current || null;
};

export const purchasePackage = async (pkg) => {
  const mod = await getPurchases();
  if (!mod) throw new Error('IAP not available on this platform');
  return await mod.Purchases.purchasePackage({ aPackage: pkg });
};

export const restorePurchases = async () => {
  const mod = await getPurchases();
  if (!mod) throw new Error('IAP not available on this platform');
  return await mod.Purchases.restorePurchases();
};

export const getCustomerInfo = async () => {
  const mod = await getPurchases();
  if (!mod) return null;
  try {
    const result = await mod.Purchases.getCustomerInfo();
    return result?.customerInfo || result;
  } catch {
    return null;
  }
};

export const logoutRevenueCat = async () => {
  const mod = await getPurchases();
  if (!mod) return;
  try { await mod.Purchases.logOut(); _configured = false; } catch { /* noop */ }
};

/** Server-side reconciliation. Call after a successful purchase or restore so
 *  the backend's users.premium_status reflects the new state even if the
 *  webhook hasn't arrived yet. */
export const syncPremiumWithBackend = async () => {
  try {
    const r = await axios.post(`${API}/premium/sync`, {}, { withCredentials: true });
    return r.data;
  } catch (e) {
    return null;
  }
};
